/**
 * @desc 注册监听与 destroy
 * @author Kody
 */
import React, { createElement, useState, useCallback, useEffect } from 'react';

import { EventEmitter } from 'events';

const eventEmitterInstance = new EventEmitter()
type IEventHandle = (...args: any[]) => void;

const useOnNotification = (event: string, handle: IEventHandle, depend: React.DependencyList = []) => {
    const useErrHandle = useCallback((err: any) => {
        console.warn(err);
    }, []);
    useEffect(() => {
        eventEmitterInstance.on.call(eventEmitterInstance, event, handle);
        eventEmitterInstance.on.call(eventEmitterInstance, 'error', useErrHandle);
        return () => {
            eventEmitterInstance.removeListener.call(eventEmitterInstance, event, handle);
            eventEmitterInstance.removeListener.call(eventEmitterInstance, 'error', useErrHandle);
        };
    }, depend);
};

export const eventEmitter = {
    emit: eventEmitterInstance.emit.bind(eventEmitterInstance),
    once: eventEmitterInstance.once.bind(eventEmitterInstance),
    on: useOnNotification,
    removeListener: eventEmitterInstance.removeListener.bind(eventEmitterInstance),
};

enum EventType {
    on = 'on',
    once = 'once',
}

interface IListener {
    [name: string]: {
        type: EventType;
        handle: IEventHandle;
    };
}

const defaultSubscription = {
    error: {
        type: 'on' as EventType,
        handle: (err: unknown) => {
            console.warn(err);
        },
    },
};

export interface INotificationComponentProps {
    broadcast(action: string, ...args: any[]): void; // 广播消息
    once(event: string, handle: IEventHandle): void; // 订阅一次
    subscription(event: string, handle: IEventHandle): void; // 订阅
}

const allowType = ['on', 'once'];

type IWrapProps<T> = Pick<T, Exclude<keyof T, 'broadcast' | 'subscription' | 'once'>>;

export function withNotification<
    P extends INotificationComponentProps,
    C extends React.ComponentType<React.PropsWithChildren<P>>
>(component: C & React.ComponentType<React.PropsWithChildren<P>>) {
    const NotificationWrap = (props: IWrapProps<P>) => {
        const [subscription, setSubscription] = useState<IListener>(defaultSubscription);
        const vilidateListener = (type: EventType) => {
            return allowType.indexOf(type) !== -1;
        };
        useEffect(() => {
            for (const key in subscription) {
                if (Object.prototype.hasOwnProperty.call(subscription, key)) {
                    const { type, handle } = subscription[key];
                    if (vilidateListener(type)) {
                        eventEmitterInstance[type](key, handle);
                    }
                }
            }
            return () => {
                for (const key in subscription) {
                    if (Object.prototype.hasOwnProperty.call(subscription, key)) {
                        const { type, handle } = subscription[key];
                        if (vilidateListener(type)) {
                            eventEmitterInstance.removeListener(key, handle);
                        }
                    }
                }
            };
        }, [subscription]);

        const extendProps = {
            broadcast: (action: string, ...args: any[]) => {
                eventEmitterInstance.emit(action, ...args);
            },
            subscription: (event: string, handle: IEventHandle) => {
                setSubscription(Object.assign({}, subscription, {[event]: {type: 'on', handle}}));
            },
            once: (event: string, handle: IEventHandle) => {
                setSubscription(Object.assign({}, subscription, {[event]: {type: 'once', handle}}));
            },
            remove: (event: string) => {
                const newSubscription = Object.assign({}, subscription);
                delete newSubscription[event];
                setSubscription(newSubscription);
            },
        };

        const childProps = Object.assign({}, props, extendProps) as unknown as React.Attributes & P;
        return createElement(component, childProps);
    };

    // tslint:disable-next-line:max-line-length
    return NotificationWrap;
};
